import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "./axiosConfig";
import IframeResizer from "iframe-resizer-react";
import {
  HiBeaker,
  HiChartBar,
  HiChatAlt2,
  HiSparkles,
  HiLightningBolt,
  HiPlay,
  HiOutlineCheck,
  HiChevronDown,
  HiStar,
  HiDocumentText,
  HiUserGroup,
  HiChevronRight,
} from "react-icons/hi";
import { Sparkles, FileText, ArrowRight, MessageSquare, Video, Target, ChevronLeft, Users, Star, ChevronRight } from "lucide-react";
import EnhancedHeader from "./EnhancedHeader";
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 },
};

const formatDate = (dateString) => {
  const options = { year: "numeric", month: "long", day: "numeric" };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

const CreditCard = ({ icon: Icon, title, count, description, color }) => (
  <div
    className="bg-white rounded-xl p-6 border border-gray-200 hover:border-gray-300 transition-all duration-300
                  hover:shadow-lg"
  >
    <div className="flex items-center mb-3">
      <div className={`w-10 h-10 ${color} rounded-full flex items-center justify-center mr-3`}>
        <Icon className="w-5 h-5 text-gray-800" />
      </div>
      <h3 className="font-medium text-gray-900">{title}</h3>
    </div>
    <p className={`text-3xl font-bold mb-1 ${color.replace("bg-", "text-")}`}>{count}</p>
    <p className="text-sm text-gray-500">{description}</p>
  </div>
);

function PricingCard({
  plan,
  price,
  description,
  benefits,
  linkTo,
  environment,
  isPopular = false,
  discountInfo,
  originalPrice,
  currencySymbol = "$",
}) {
  const hasDiscount = discountInfo && discountInfo.discount;
  const pricingMessage = hasDiscount ? `${discountInfo.countryName} - ${discountInfo.discount}% off` : "";
  const finalLinkTo = hasDiscount ? `${linkTo[environment]}&discount_code=${discountInfo.code}` : linkTo[environment];

  return (
    <div
      className={`relative bg-white rounded-xl ${
        isPopular ? "border-2 border-green-500 shadow-xl" : "border border-gray-200"
      } overflow-hidden`}
    >
      {isPopular && (
        <div className="absolute top-0 right-0 bg-green-500 text-white px-3 py-1 rounded-bl-lg text-sm font-medium">Most Popular</div>
      )}
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{plan}</h3>
        <div className="flex items-baseline mb-4">
          <span className="text-3xl font-bold text-gray-900">
            {currencySymbol}
            {price}
          </span>
          <span className="text-gray-500 ml-1">/month</span>
          {originalPrice && hasDiscount && <span className="ml-2 text-lg text-gray-500 line-through">${originalPrice}</span>}
        </div>
        {pricingMessage && (
          <div className="mb-4 py-1 px-3 bg-green-50 text-green-800 text-sm rounded-lg inline-block">{pricingMessage}</div>
        )}
        <p className="text-gray-600 mb-8">{description}</p>
        <hr className="my-6 border-gray-200" />
        <ul className="space-y-4">
          {benefits.map((benefit, idx) => (
            <li key={idx} className="flex items-start">
              <HiOutlineCheck className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
              <span className="text-gray-700">{benefit}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="p-6 bg-gray-50 mt-6">
        <a
          href={finalLinkTo}
          className={`block w-full py-3 px-4 text-center rounded-lg font-medium transition-colors ${
            isPopular ? "bg-green-500 hover:bg-green-600 text-white" : "bg-white border border-gray-300 hover:border-gray-400 text-gray-900"
          }`}
        >
          Buy Plan
        </a>
      </div>
    </div>
  );
}

const Credits = () => {
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState({});
  const [planName, setPlanName] = useState("");
  const [portalUrl, setPortalUrl] = useState("");
  const [email, setEmail] = useState("");
  const [userID, setUserID] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [discountInfo, setDiscountInfo] = useState({});
  const [planStatus, setPlanStatus] = useState("");
  const [isFreePlan, setIsFreePlan] = useState(false);
  const [isCanceled, setIsCanceled] = useState(false);
  const [planDate, setPlanDate] = useState("");
  const formattedDate = formatDate(planDate);
  const environment = process.env.REACT_APP_ENV;

  // Static Pricing Config
  const planConfigs = {
    starter: {
      price: 59,
      basePrice: 59,
      description: "FOR TESTING THE WATERS",
      benefits: [
        // "Daily Ideation",
        // "10 AI User Interviews",
        // "10 AI UGC videos/month",
        // "10 leads/month",
        // "No Business Report",
        // "No Community Access (Coming Soon)",

        // "Daily Ideation",
        // "10 videos per month",
        // "25 Ideation Credits",
        // // "10 AI User Interviews",
        // "Export 10  videos/month",
        // "Access to all 50+ AI avatars",
        // "Business Report : Executive Summary Access",

        // // "❌ Can't create UGC avatars",
        // // "10 leads/month",
        // "❌ No Full Business Report Generation",
        // "❌ No Community Access (Coming Soon)",
        // "10 videos per month",
        // "All 50+ AI avatars",
        // "Preview of business reports",
        // "25 product idea searches",
        // "20 Idea discovery credits",
        // // "10 AI User Interviews",
        // "10 AI marketing videos per month",
        // "20 Lead discovery credits",
        // "Track 3 Keywords (Reddit + Twitter + LinkedIn)",
        // "Full access to all 50+ AI avatars",
        // "1 Executive Summary Report (Lite Version)",
        // "❌ No Full Business Plan",
        // "❌ No Export as PDF",

        "25 Idea discovery credits",
        // "10 AI User Interviews",
        "25 AI marketing videos per month",
        "30 Lead discovery credits",
        "Track 5 Keywords (Reddit + Twitter + LinkedIn)",
        "Full access to all 50+ AI avatars",
        // "1 Executive Summary Report (Lite Version)",
        "2 Full 100+ Page Business Reports",
        "Export Full Report as PDF/Docs",
      ],
      linkTo: {
        // local: `https://feedbackbyai1.lemonsqueezy.com/buy/dc41548d-d2a4-4f7e-8efd-f876d8f9a113?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Starter`,
        // production: `https://feedbackbyai1.lemonsqueezy.com/buy/6789d080-c650-4d7d-83a1-5a5083c09d04?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Starter`,
        // the below is 19 usd plan
        // local: `https://feedbackbyai1.lemonsqueezy.com/buy/4d610dd9-7dd0-494b-8b84-ac0f1480125a?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Starter`,
        // production: `https://feedbackbyai1.lemonsqueezy.com/buy/1514f65a-1925-48ea-8ecd-28e4475986a2?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Starter`,

        local: `https://feedbackbyai1.lemonsqueezy.com/buy/43bb3e9c-7f9e-4a15-8dab-52cbcbe6be1f?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Starter`,
        production: `https://feedbackbyai1.lemonsqueezy.com/buy/b3ccc8cf-c7c7-4bda-aa59-67667423a503?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Starter`,
      },
    },

    select: {
      price: 95,
      basePrice: 95,
      description: "FOR SERIOUS BUSINESS BUILDERS",
      benefits: [
        // "Daily Ideation",
        // "50 AI User Interviews",
        // "50 UGC videos/month",
        // "50 leads/month",
        // "1 Full 100+ Page Business Report",
        // "Community Access (coming soon)",
        // "50 Ideation Credits",
        // "Export 50 videos/month",
        // "Access to all 50+ AI avatars",
        // // "Create your own AI avatars (25 videos)",
        // // "50 AI User Interviews",
        // // "50 leads/month",
        // "1 Full 100+ Page Business Report",
        // "Export Full Report as PDF/Docs",

        // "50 videos per month",
        // "All 50+ AI avatars",
        // "1 full business report",
        // "50 product idea searches",
        // "Export as PDF/Doc",
        // "25 Idea discovery credits",
        // "25 AI marketing videos per month",
        // "50 Lead discovery credits",
        // "Track 5 Keywords (Reddit + Twitter + LinkedIn)",
        // "Full access to all 50+ AI avatars",
        // // "Create your own AI avatars (25 videos)",
        // // "50 AI User Interviews",
        // // "50 leads/month",
        // // "Complete 100+ page business report",
        // "1 Full 100+ Page Business Report",
        // "Export reports as PDF/Docs",
        // "Community Access",

        "25 Idea discovery credits",
        "25 AI marketing videos per month",
        "75 Lead discovery credits",
        "Track 7 Keywords (Reddit + Twitter + LinkedIn)",
        "Full access to all 50+ AI avatars",

        // "Create your own AI avatars (100 videos)",
        // "150 AI User Interviews",
        // "100 AI UGC videos/month",

        // "300 leads/month",
        "3 Full 100+ Page Business Reports",
        "Export Full Report as PDF/Docs",
      ],
      linkTo: {
        // local: `https://feedbackbyai1.lemonsqueezy.com/buy/dc41548d-d2a4-4f7e-8efd-f876d8f9a113?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Select`,
        // production: `https://feedbackbyai1.lemonsqueezy.com/buy/6789d080-c650-4d7d-83a1-5a5083c09d04?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Select`,

        local: `https://feedbackbyai1.lemonsqueezy.com/buy/b096120f-36f0-47d9-b8db-a13f76ec0b93?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Select`,
        production: `https://feedbackbyai1.lemonsqueezy.com/buy/b1cc5601-03a3-4ca8-ba5b-c7f51ac4c426?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Select`,
      },
    },

    pro: {
      price: 149,
      basePrice: 149,
      description: "FOR SCALING BUSINESS",
      benefits: [
        // "Daily Ideation",
        // "50 AI User Interviews",
        // "50 UGC videos/month",
        // "50 leads/month",
        // "1 Full 100+ Page Business Report",
        // "Community Access (coming soon)",
        // "50 Ideation Credits",
        // "Export 50 videos/month",
        // "Access to all 50+ AI avatars",
        // // "Create your own AI avatars (25 videos)",
        // // "50 AI User Interviews",
        // // "50 leads/month",
        // "1 Full 100+ Page Business Report",
        // "Export Full Report as PDF/Docs",

        // "50 videos per month",
        // "All 50+ AI avatars",
        // "1 full business report",
        // "50 product idea searches",
        // "Export as PDF/Doc",
        // "25 Idea discovery credits",
        // "50 AI marketing videos per month",
        // "100 Lead discovery credits",
        // "Track 10 Keywords (Reddit + Twitter + LinkedIn)",
        // "Full access to all 50+ AI avatars",
        // // "Create your own AI avatars (100 videos)",
        // // "150 AI User Interviews",
        // // "100 AI UGC videos/month",

        // // "300 leads/month",
        // "2 Full 100+ Page Business Reports",
        // "Export Full Report as PDF/Docs",

        "25 Idea discovery credits",
        "50 AI marketing videos per month",
        "150 Lead discovery credits",
        "Track 10 Keywords (Reddit + Twitter + LinkedIn)",
        "Full access to all 50+ AI avatars",
        // "Create your own AI avatars (100 videos)",
        // "150 AI User Interviews",
        // "100 AI UGC videos/month",

        // "300 leads/month",
        "5 Full 100+ Page Business Reports",
        "Export Full Report as PDF/Docs",

        // "Community Access",
      ],
      linkTo: {
        local: `https://feedbackbyai1.lemonsqueezy.com/buy/3987c415-d84d-4533-a9e5-f9dd259be2ba?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Pro`,
        production: `https://feedbackbyai1.lemonsqueezy.com/buy/8883553b-49cc-4b19-a32b-12d919e09f3a?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Pro`,
      },
    },

    // select: {
    //   price: 59,
    //   basePrice: 59,
    //   description: "FOR SERIOUS BUSINESS BUILDERS",
    //   benefits: [
    //     // "Daily Ideation",
    //     // "50 AI User Interviews",
    //     // "50 UGC videos/month",
    //     // "50 leads/month",
    //     // "1 Full 100+ Page Business Report",
    //     // "Community Access (coming soon)",
    //     // "50 Ideation Credits",
    //     // "Export 50 videos/month",
    //     // "Access to all 50+ AI avatars",
    //     // // "Create your own AI avatars (25 videos)",
    //     // // "50 AI User Interviews",
    //     // // "50 leads/month",
    //     // "1 Full 100+ Page Business Report",
    //     // "Export Full Report as PDF/Docs",

    //     "50 videos per month",
    //     "All 50+ AI avatars",
    //     "1 full business report",
    //     "50 product idea searches",
    //     "Export as PDF/Doc",
    //     // "Community Access",
    //   ],
    //   linkTo: {
    //     local: `https://feedbackbyai1.lemonsqueezy.com/buy/43bb3e9c-7f9e-4a15-8dab-52cbcbe6be1f?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Select`,
    //     production: `https://feedbackbyai1.lemonsqueezy.com/buy/b3ccc8cf-c7c7-4bda-aa59-67667423a503?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Select`,
    //   },
    // },
    // select: {
    //   price: 95,
    //   basePrice: 95,
    //   benefits: [
    //     // "AI Idea Discovery: 5 search/day",
    //     "100 Ideation Credits",

    //     "50 AI User Interviews",
    //     "50 AI UGC videos/month",
    //     "100 leads/month",
    //     "2 Full 100+ Page Business Reports",
    //     "Community Access (Coming Soon)",
    //   ],
    //   linkTo: {
    // local: `https://feedbackbyai1.lemonsqueezy.com/buy/b096120f-36f0-47d9-b8db-a13f76ec0b93?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Select`,
    // production: `https://feedbackbyai1.lemonsqueezy.com/buy/b1cc5601-03a3-4ca8-ba5b-c7f51ac4c426?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Select`,
    //   },
    // },

    // pro: {
    //   price: 95,
    //   basePrice: 95,
    //   benefits: [
    //     "Daily Ideation",
    //     "100 AI User Interviews",
    //     "100 AI UGC videos/month",
    //     "100 leads/month",
    //     "2 Full 100+ Page Business Report",
    //     "Community Access",
    //   ],
    //   linkTo: {
    //     local: `https://feedbackbyai1.lemonsqueezy.com/buy/b096120f-36f0-47d9-b8db-a13f76ec0b93?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Pro`,
    //     production: `https://feedbackbyai1.lemonsqueezy.com/buy/b1cc5601-03a3-4ca8-ba5b-c7f51ac4c426?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Pro`,
    //   },
    // },

    // pro: {
    //   price: 248,
    //   basePrice: 248,
    //   description: "FOR MULTI-PRODUCT BUSINESSES",
    //   benefits: [
    //     // "AI Idea Discovery: 10 search/day",
    //     // "Daily Ideation",
    //     // "100 AI User Interviews",
    //     // "100 UGC videos/month",
    //     // "100 leads/month",
    //     // "2 Full 100+ Page Business Report",
    //     // "Community Access (coming soon)",
    //     // "250 Ideation Credits",
    //     // "100 Ideation Credits",
    //     // "Export 250 videos/month",
    //     // "Access to all 50+ AI avatars",
    //     // // "Create your own AI avatars (100 videos)",
    //     // // "150 AI User Interviews",
    //     // // "100 AI UGC videos/month",

    //     // // "300 leads/month",
    //     // "5 Full 100+ Page Business Reports",
    //     // "Export Full Report as PDF/Docs",
    //     // "Community Access (Coming Soon)",

    //     "250 videos per month",
    //     "All 50+ AI avatars",
    //     "5 full business reports",
    //     "100 product idea searches",
    //     "Priority support",
    //   ],
    //   linkTo: {
    //     local: `https://feedbackbyai1.lemonsqueezy.com/buy/1039a42c-4175-4729-9cab-eb089d2a222c?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Pro`,
    //     production: `https://feedbackbyai1.lemonsqueezy.com/buy/2c495bb1-1f63-4ba7-811c-b7b8e031db39?checkout[email]=${email}&checkout[custom][userId]=${userID}&checkout[custom][plan]=Pro`,
    //   },
    // },
  };

  // API call effects
  useEffect(() => {
    const token = localStorage.getItem("access");
    if (!token) {
      window.location.href = "/signin/";
      return;
    }
  }, []);

  useEffect(() => {
    const fetchUserEmailAndID = async () => {
      try {
        const response = await api.get("get-user-email/");
        const { email, id } = response.data;
        setEmail(email);
        setUserID(id);
      } catch (error) {
        console.error("Error fetching user email and ID:", error);
      }
    };
    fetchUserEmailAndID();
  }, []);

  useEffect(() => {
    const fetchUserCredits = async () => {
      try {
        const response = await api.get("get-user-credits/");
        if (response.data) {
          setPlanName(response.data.plan_name);
          setPlanStatus(response.data.subscription_active ? "active" : "inactive");
          setPlanDate(response.data.end_date);
          setUsage({
            question_credits: response.data.question_credits || 0,
            report_credits: response.data.report_credits || 0,
            video_credits: response.data.video_credits || 0,
            ideation_credits: response.data.ideation_credits || 0,
            lead_credits: response.data.lead_credits || 0,
          });
          setIsFreePlan(response.data.plan_name === "Free");
          setIsCanceled(!response.data.subscription_active);
        }
      } catch (error) {
        console.error("Error fetching user credits:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserCredits();
  }, []);

  useEffect(() => {
    const fetchCustomerPortalURL = async () => {
      try {
        const response = await api.get("customer-portal-url/");
        if (response.data?.portal_url) {
          setPortalUrl(response.data.portal_url);
        }
      } catch (error) {
        console.error("Error fetching customer portal URL:", error);
      }
    };
    fetchCustomerPortalURL();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-12 h-12 border-t-3 border-green-600 border-solid rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple Header - Matching the dashboard page */}
      {/* <header className="h-16 border-b border-gray-100 bg-white shadow-sm flex items-center px-8 sticky top-0 z-10">
        <div className="max-w-6xl w-full mx-auto flex justify-between items-center">
          <span className="font-semibold text-xl text-green-600 tracking-tight">FeedbackbyAI</span>
          <Link to="/dashboard/" className="text-gray-600 hover:text-green-600 transition-colors flex items-center">
            <ArrowRight className="h-4 w-4 mr-1 transform rotate-180" />
            Back to Dashboard
          </Link>
        </div>
      </header> */}
      <EnhancedHeader />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div {...fadeInUp}>
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Credits & Subscription</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">Monitor your credit usage and manage your subscription plan</p>
          </div>

          {/* Current Plan Status */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-12">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{planName} Plan</h2>
                <p className="text-gray-600 mt-1">{planStatus === "active" && planDate && `Renews on ${formattedDate}`}</p>
              </div>
              {portalUrl && (
                <a
                  href={portalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Manage Subscription
                </a>
              )}
            </div>

            {/* Credit Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-4 gap-6">
              <CreditCard
                icon={FileText}
                title="100+ Page Report Credits"
                count={usage.report_credits}
                description="Generate comprehensive business plans and validate ideas with clarity."
                color="bg-green-100"
              />

              <CreditCard
                icon={Target}
                title="LeadFinder Credits"
                count={usage.lead_credits}
                description="Find warm, high-intent leads from Reddit, Twitter, and LinkedIn — skip the cold outreach."
                color="bg-red-100"
              />

              <CreditCard
                icon={Video}
                title="TikTok/Reels Video Credits"
                count={usage.video_credits}
                description="Create scroll-stopping short videos using AI — perfect for TikTok and Instagram.."
                color="bg-orange-100"
              />

              <CreditCard
                icon={Sparkles}
                title="Brainstorming Credits"
                count={usage.ideation_credits}
                description="Discover breakthrough ideas by tapping into real-time market conversations."
                color="bg-purple-100"
              />
              {/* <CreditCard
                icon={MessageSquare}
                title="Interview Credits"
                count={usage.question_credits}
                description="For AI user interviews"
                color="bg-blue-100"
              /> */}
            </div>
          </div>

          {/* Available Plans */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Available Plans</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <PricingCard {...planConfigs.starter} plan="Starter" discountInfo={discountInfo} environment={environment} />
              <PricingCard {...planConfigs.select} plan="Select" isPopular={true} discountInfo={discountInfo} environment={environment} />
              <PricingCard {...planConfigs.pro} plan="Pro" discountInfo={discountInfo} environment={environment} />
            </div>
          </div>

          {/* Testimonials */}
          <div className="mb-12">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900">What Our Users Say</h2>
              <p className="text-gray-600 mt-2">Join thousands of satisfied entrepreneurs</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <IframeResizer
                src="https://embed-v2.testimonial.to/w/feedbackbyai"
                style={{ width: "1px", minWidth: "100%" }}
                className="rounded-xl"
              />
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Credits;
